import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Stack,
  Text,
  Flex,
  Icon,
  Box,
  Tag,
  TagLabel,
  Divider,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Wrap,
  WrapItem,
  Badge,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, CloseIcon } from '@chakra-ui/icons';

const EnvioInformeResultModal = ({ isOpen, onClose, payload, state }) => {
  const data = payload || {};
  const okTotal = data.sent_count ?? data.ok_total ?? 0;
  const failTotal = data.error_count ?? data.fail_total ?? 0;
  const statusSummary = data.status_summary;

  let statusMeta = { icon: CheckCircleIcon, color: 'green.500', title: 'Envío completado' };
  if (statusSummary === "failed" || okTotal === 0) {
    statusMeta = { icon: CloseIcon, color: 'red.500', title: 'Envío fallido' };
  } else if (statusSummary === "partial" || failTotal > 0) {
    statusMeta = { icon: WarningIcon, color: 'orange.400', title: 'Envío parcial' };
  }
  if (state === 'FAILURE') {
    statusMeta = { icon: CloseIcon, color: 'red.500', title: 'Envío fallido' };
  } else if (state === 'PARTIAL_SUCCESS') {
    statusMeta = { icon: WarningIcon, color: 'orange.400', title: 'Envío parcial' };
  }

  const lockSummary = data.lock_summary || {};
  const resultados = Array.isArray(data.resultados) ? data.resultados : [];
  const explicitFails = Array.isArray(data.fallos) ? data.fallos : [];
  const okItems = resultados.filter((item) => item && item.ok);
  const fallbackFails = resultados
    .filter((item) => item && !item.ok)
    .map((item, idx) => ({
      uf: item.uf,
      destino: item.mail || item.telefono || item.destino,
      error: item.error,
      _key: `${item.uf || idx}-fallback`,
    }));
  const failItems =
    explicitFails.length > 0
      ? explicitFails.map((item, idx) => ({ ...item, _key: `${item.uf || idx}-explicit` }))
      : fallbackFails;
  const failCountDisplay = explicitFails.length || fallbackFails.length;
  const showOkTruncated = Boolean(data.resultados_truncated);
  const showFailTruncated =
    Boolean(data.fallos_truncated) || (explicitFails.length === 0 && Boolean(data.resultados_truncated));

  const formatLabel = (value) => {
    if (!value) return 'Período';
    return value
      .toString()
      .trim()
      .replace(/_/g, ' ')
      .toLocaleLowerCase('es-AR')
      .replace(/\b\w/g, (char) => char.toLocaleUpperCase('es-AR'));
  };

  const periodLabel = formatLabel(lockSummary.periodo_label);
  const nextPeriodLabel = formatLabel(lockSummary.periodo_abierto?.label);

  const formatUf = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num.toFixed(2);
    }
    return String(value);
  };

  const renderChannelRow = (label, key) => (
    <Flex key={key} justify="space-between" fontSize="sm">
      <Text fontWeight="medium">{label}</Text>
      <Text>
        {data[`ok_${key}`] || 0} OK · {data[`fail_${key}`] || 0} fallidos
      </Text>
    </Flex>
  );

  const periodLocked = Boolean(lockSummary.period_locked);
  const nuevoPeriodo = lockSummary.periodo_abierto;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader>Resultado del envío</ModalHeader>
        <ModalBody>
          <Stack spacing={4}>
            <Flex align="center" gap={3}>
              <Icon as={statusMeta.icon} boxSize={6} color={statusMeta.color} />
              <Text fontSize="lg" fontWeight="semibold" color={statusMeta.color}>
                {statusMeta.title}
              </Text>
            </Flex>
            <Stack spacing={2} borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
              {renderChannelRow('Email', 'mail')}
              {renderChannelRow('WhatsApp', 'whatsapp')}
              <Divider />
              <Flex justify="space-between" fontWeight="bold">
                <Text>Total enviados</Text>
                <Text>
                  {okTotal} OK · {failTotal} fallidos
                </Text>
              </Flex>
            </Stack>

            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Text fontWeight="semibold" mb={1}>
                Detalle por UF
              </Text>
              <Tabs variant="soft-rounded" colorScheme="blue" size="sm">
                <TabList>
                  <Tab>Enviadas ({okItems.length})</Tab>
                  <Tab>Con error ({failCountDisplay})</Tab>
                </TabList>
                <TabPanels pt={3}>
                  <TabPanel px={0}>
                    {okItems.length > 0 ? (
                      <Box maxH="220px" overflowY="auto">
                        <Wrap spacing={2}>
                          {okItems.map((item, idx) => (
                            <WrapItem key={`${item.uf || idx}-ok`}>
                              <Tag colorScheme="green" variant="subtle">
                                UF {formatUf(item.uf)}
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        No se registraron UFs enviadas correctamente.
                      </Text>
                    )}
                    {showOkTruncated && (
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Mostrando un resumen (lista truncada).
                      </Text>
                    )}
                  </TabPanel>
                  <TabPanel px={0}>
                    {failItems.length > 0 ? (
                      <Box maxH="220px" overflowY="auto">
                        <Stack spacing={3}>
                          {failItems.map((f, idx) => (
                            <Box
                              key={f._key || `${f.uf || idx}-fail`}
                              borderWidth="1px"
                              borderRadius="md"
                              p={3}
                            >
                              <Flex justify="space-between" align="center" mb={1}>
                                <Badge colorScheme="red">UF {formatUf(f.uf)}</Badge>
                                <Text fontSize="xs" color="gray.500">
                                  {f.destino || f.mail || f.telefono || "Sin destino"}
                                </Text>
                              </Flex>
                              {f.error && (
                                <Text fontSize="sm" color="red.500" noOfLines={2}>
                                  {f.error}
                                </Text>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        No hubo UFs con error.
                      </Text>
                    )}
                    {showFailTruncated && (
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Mostrando un resumen (lista truncada).
                      </Text>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>

            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Text fontWeight="semibold" mb={1}>
                Estado del período
              </Text>
              {okTotal === 0 ? (
                <Stack spacing={1}>
                  <Text>
                    ℹ️ El período no se cerró porque no hubo envíos exitosos.
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Revisá los errores y reintentá el envío.
                  </Text>
                </Stack>
              ) : periodLocked ? (
                <Stack spacing={2}>
                  <Text>
                    🔒 El período <b>{periodLabel}</b> quedó cerrado y bloqueado.
                  </Text>
                  {lockSummary.locked_reason && (
                    <Tag width="fit-content" colorScheme="purple">
                      <TagLabel>{formatLabel(lockSummary.locked_reason)}</TagLabel>
                    </Tag>
                  )}
                  {nuevoPeriodo && (
                    <Text fontSize="sm" color="gray.600">
                      ✅ Se abrió automáticamente el nuevo período <b>{nextPeriodLabel}</b>.
                    </Text>
                  )}
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <Text>
                    ✅ El envío tuvo destinatarios exitosos, pero no pudimos confirmar el cierre automático del período.
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Verificá manualmente el estado del período o reintentá el cierre desde la sección de Períodos.
                  </Text>
                </Stack>
              )}
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Entendido
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EnvioInformeResultModal;
